import { Loader, LoadingOverlay } from "@mantine/core";
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateOrder } from "./useCreateOrder";
import { useIpcContext } from "/@/shared/contexts/IpcContext";
import { useLocation } from "react-router-dom";
import { CreateProductPage } from "/@/pages/products/create/CreateProductPage";
import {
  CreateAnatomyItemStep,
  CreateOrderStep,
} from "/@/shared/types/orders/createOrder.types";
import { CreateOrderPatientInfo } from "/@/pages/orders/create/CreateOrderPatientInfo";
import { CreateOrderReview } from "../CreateOrderReview";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import {
  ItemType,
  GetOrderEstimatedDeliveryDateDocument,
} from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import {
  UPDATE_ORDER,
  CREATE_REMOVABLE_ITEM,
  DELETE_MANY_REMOVABLE_ITEMS,
  UPDATE_PRODUCT,
  UPDATE_DELIVERY_DATE,
  UpperArchTeeth,
  LowerArchTeeth,
  ArchTypeEn,
  OrderStatus,
  getLastShippingDateFromDeliveryDate,
  Order,
  OrderComment,
  isBridgeAndInlayCoreOverlapping,
  OrderCommentType,
  NEW_PAP_WORKFLOW_ACTIVATION_DATE,
  WORKFLOW_MAP,
} from "@jasper/shared";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useOldStepperStore } from "/@/shared/stores/orders/stepper-store.store";
import { useV2ProductTypes } from "@jasper/product-workflows";
import { ProductV2Adapter } from "./ProductV2Adapter";

export const CreateOrderPage: FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { existingDraftOrderLoading, order, refetchOrder } = useCreateOrder(
    state ? { newOrderId: state.id } : undefined
  );
  const { user } = useAuthContext();
  const [updateOrder] = useMutation(UPDATE_ORDER);
  const [updateProduct] = useMutation(UPDATE_PRODUCT);
  const [createRemovableItem] = useMutation(CREATE_REMOVABLE_ITEM);
  const [deleteManyRemovableItem] = useMutation(DELETE_MANY_REMOVABLE_ITEMS);
  const [updateDeliveryDate] = useMutation(UPDATE_DELIVERY_DATE);
  const { creatingOrderId, setCreatingOrderId } = useIpcContext();
  const [
    newOrderId,
    createOrderStep,
    createProductStep,
    createProductIndex,
    updateNewOrderId,
    updateCreateOrderStep,
    updateProductNewWFStep,
    updateCreateProductStep,
    updateCreateProductIndex,
  ] = useCreateOrderStore(state => [
    state.newOrderId,
    state.createOrderStep,
    state.createProductStep,
    state.createProductIndex,
    state.updateNewOrderId,
    state.updateCreateOrderStep,
    state.updateProductNewWFStep,
    state.updateCreateProductStep,
    state.updateCreateProductIndex,
  ]);

  const { clickedButton } = useOldStepperStore();

  const [isLoading, setIsLoading] = useState<boolean | null>(null);

  const [getDeliveryDate] = useLazyQuery(GetOrderEstimatedDeliveryDateDocument);

  const { isV2Product } = useV2ProductTypes();

  const getCommentBlock = (order: Order, orderComment: OrderComment) => {
    const existingOrderComment = (order?.orderComment ?? []).find(
      comment => comment.type === OrderCommentType.ORDER
    );
    let commentBlock = undefined;
    if (existingOrderComment) {
      commentBlock = {
        update: [
          {
            data: {
              comment: {
                set: orderComment,
              },
            },
            where: {
              id: existingOrderComment?.id,
            },
          },
        ],
      };
    } else if (orderComment) {
      commentBlock = {
        create: [
          {
            comment: orderComment,
          },
        ],
      };
    }
    return commentBlock;
  };

  const isCustomTrayCreatedFromPartialDentureWorkflow = (
    order: Order,
    createOrderStep: CreateOrderStep,
    createProductIndex: number
  ) =>
    order?.products &&
    createOrderStep === CreateOrderStep.Product &&
    createProductIndex < order.products.length &&
    order.products[createProductIndex]?.productType === ItemType.CustomTray;

  useEffect(() => {
    if (
      order?.status !== undefined &&
      (order?.status as unknown as OrderStatus) !== OrderStatus.DRAFT
    ) {
      navigate(`/order/${order.id}`);
    }
    if (newOrderId === null && order) {
      updateNewOrderId(order.id);
      return;
    }

    if (order?.id && newOrderId !== order.id) {
      /*
       * Applied only during new order creation.
       * Does not affect the "add a product" button or navigating to the next product on the recap page.
       */
      updateCreateProductStep(0);
      updateNewOrderId(order.id);
      return;
    }

    if (
      (order?.products?.length ?? 0) > 0 &&
      new Date(order?.createdAt ?? undefined)?.getTime() >
        NEW_PAP_WORKFLOW_ACTIVATION_DATE.getTime() &&
      state?.comingFrom !== "create-new-order"
    ) {
      const currentProduct = order?.products?.[createProductIndex];
      if (
        createOrderStep === CreateOrderStep.Patient &&
        createProductIndex > 0
      ) {
        updateCreateOrderStep(CreateOrderStep.Product);
        return;
      }

      if (
        createOrderStep === CreateOrderStep.Product &&
        (currentProduct?.productType === ItemType.PartialDenture ||
          currentProduct?.productType === ItemType.FullDenture)
      ) {
        if (clickedButton === "PREVIOUS") {
          const PAPWorkflowLength =
            WORKFLOW_MAP.PARTIAL_DENTURE.steps().length +
            (createProductIndex === 0 ? 1 : 0);
          updateProductNewWFStep(PAPWorkflowLength - 1);
        } else {
          updateProductNewWFStep(0);
        }
        navigate("/create-new-order", {
          state: {
            id: order?.id,
            workflow: [
              ...(order?.products?.map(product => product.productType) ?? []),
            ],
            cameFromOtherProduct: true,
          },
        });
        return;
      }
    }

    if (
      order?.products &&
      createOrderStep === CreateOrderStep.Product &&
      createProductStep === CreateAnatomyItemStep.Preferences
    ) {
      /*
       * Bridge-on-InlayCore workflow:
       * 1) First product is the inlay-core, second is the bridge.
       * 2) Skip the Bridge preferences step since InlayCore covers them.
       * 3) Prevents user confusion over treating them as two separate products.
       */
      const bridgeAndInlayCoreProducts = order.products.filter(
        product =>
          product.productType === ItemType.Bridge ||
          product.productType === ItemType.InlayCore
      );

      if (
        bridgeAndInlayCoreProducts.length === 2 &&
        isBridgeAndInlayCoreOverlapping(order.products) &&
        createProductIndex === 1
      ) {
        if (order.products.length === 2) {
          updateCreateOrderStep(CreateOrderStep.Review);
        }
        return;
      }
    }

    if (
      isCustomTrayCreatedFromPartialDentureWorkflow(
        order,
        createOrderStep,
        createProductIndex
      )
    ) {
      /*
       * CUSTOM_TRAY products are automatically created by the PAP workflow (PEI option).
       * They don't require user configuration, so skip to the next product.
       */
      updateCreateProductIndex(createProductIndex + 1);
      return;
    }

    if (
      order?.products &&
      createOrderStep === CreateOrderStep.Product &&
      createProductIndex > order.products.length - 1
    ) {
      /*
       * When all products have been processed, advance to the Review step.
       */
      updateCreateOrderStep(CreateOrderStep.Review);
      return;
    }

    if (
      createOrderStep === CreateOrderStep.Review &&
      order?.products &&
      state?.comingFrom !== "create-new-order"
    ) {
      /*
       * Reset the product step to 0 when a new item is added from the Review page
       * With the "add a product" button
       */
      updateCreateProductStep(0);
      updateCreateProductIndex(order.products.length - 1);
    }
  }, [
    order,
    newOrderId,
    createOrderStep,
    createProductIndex,
    createProductStep,
  ]);

  useEffect(() => {
    setIsLoading(existingDraftOrderLoading);
  }, [existingDraftOrderLoading]);

  useEffect(() => {
    if (creatingOrderId) {
      setCreatingOrderId(null);
    }
  }, [creatingOrderId]);

  useEffect(() => {
    if (state?.openAddProduct) {
      updateCreateOrderStep(CreateOrderStep.Review);
    }
  }, [state?.openAddProduct]);

  if (isLoading || existingDraftOrderLoading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      >
        <Loader />
      </LoadingOverlay>
    );
  }
  if (!order || !order.products) {
    return (
      <div>Unable to load order data. Please try again or contact support.</div>
    );
  }
  if (order.products.length < 1) {
    return (
      <div>
        Aucun produit détecté pour cette commande. Cela peut être dû à
        l&apos;absence de produits dans le fichier source ou à un langage encore
        non reconnu par Jasper. Veuillez vérifier le contenu de votre fichier ou
        contacter l&apos;équipe Jasper pour obtenir de l&apos;assistance.
      </div>
    );
  }

  switch (createOrderStep) {
    case CreateOrderStep.Patient:
      return <CreateOrderPatientInfo order={order} />;
    case CreateOrderStep.Product: {
      const currentProduct = order?.products?.[createProductIndex];
      if (
        currentProduct &&
        isV2Product(currentProduct.productType) &&
        order?.id
      ) {
        return (
          <ProductV2Adapter
            orderId={order.id}
            productId={currentProduct.id}
            inline
            onComplete={async () => {
              await refetchOrder();
              updateCreateOrderStep(CreateOrderStep.Review);
            }}
            onClose={async () => {
              await refetchOrder();
              updateCreateOrderStep(CreateOrderStep.Review);
            }}
          />
        );
      }
      if (
        createProductIndex >= 0 &&
        createProductIndex < order.products.length
      ) {
        return (
          <CreateProductPage
            setIsLoading={setIsLoading}
            product={order.products[createProductIndex]}
            allProducts={order.products}
            orderCreationProductOrder={0}
            patient={order.patient}
            dentist={order.user}
            order={order}
            refetchOrder={refetchOrder}
            saveOrderComment={async (orderComment: string) => {
              try {
                await updateOrder({
                  variables: {
                    where: {
                      id: order.id,
                    },
                    data: {
                      comment: {
                        set: orderComment ?? null,
                      },
                      orderComment: getCommentBlock(order, orderComment),
                    },
                  },
                });
                refetchOrder();
              } catch (e) {
                console.error(e);
              }
            }}
            updateOrder={async values => {
              try {
                const product =
                  order?.products &&
                  (order?.products ?? []).length > createProductIndex
                    ? order?.products[createProductIndex]
                    : null;
                if (!product) {
                  return;
                }
                await deleteManyRemovableItem({
                  variables: {
                    where: {
                      productId: {
                        equals: product?.id,
                      },
                    },
                  },
                });
                if (
                  [
                    ItemType.RetentionWire,
                    ItemType.CustomTray,
                    ItemType.WaxBite,
                  ].includes(values.scanOnlyType)
                ) {
                  await updateProduct({
                    variables: {
                      where: {
                        id: product?.id,
                      },
                      data: {
                        teeth:
                          values?.archType === ArchTypeEn.UPPER_ARCH
                            ? UpperArchTeeth
                            : values?.archType === ArchTypeEn.LOWER_ARCH
                              ? LowerArchTeeth
                              : [...UpperArchTeeth, ...LowerArchTeeth],
                      },
                    },
                  });
                  if (
                    values?.archType === ArchTypeEn.UPPER_ARCH ||
                    values?.archType === ArchTypeEn.BOTH_ARCH
                  ) {
                    await createRemovableItem({
                      variables: {
                        args: {
                          itemType: values.scanOnlyType,
                          teeth: UpperArchTeeth,
                          teethToManufacture: {
                            set: UpperArchTeeth,
                          },
                          product: {
                            connect: {
                              id: product?.id,
                            },
                          },
                        },
                      },
                    });
                  }
                  if (
                    values?.archType === ArchTypeEn.LOWER_ARCH ||
                    values?.archType === ArchTypeEn.BOTH_ARCH
                  ) {
                    await createRemovableItem({
                      variables: {
                        args: {
                          itemType: values.scanOnlyType,
                          teeth: LowerArchTeeth,
                          teethToManufacture: {
                            set: LowerArchTeeth,
                          },
                          product: {
                            connect: {
                              id: product?.id,
                            },
                          },
                        },
                      },
                    });
                  }
                } else {
                  await createRemovableItem({
                    variables: {
                      args: {
                        itemType: values.scanOnlyType,
                        waxUpType: values.waxUpType,
                        product: {
                          connect: {
                            id: product?.id,
                          },
                        },
                      },
                    },
                  });
                }
                await refetchOrder();
              } catch (e) {
                console.error(e);
              }
            }}
            getDeliveryDate={getDeliveryDate}
            updateDeliveryDate={updateDeliveryDate}
            onSubmit={async (
              newDeliveryDateSelected: Date,
              orderComment: string,
              defaultDeliveryDate: Date
            ) => {
              try {
                const toSaveDeliveryDate = newDeliveryDateSelected
                  ? new Date(newDeliveryDateSelected)
                  : undefined;
                if (toSaveDeliveryDate) toSaveDeliveryDate.setHours(12);
                await updateOrder({
                  variables: {
                    where: {
                      id: order.id,
                    },
                    data: {
                      confirmedAt: {
                        set: Date.now(),
                      },
                      comment: {
                        set: orderComment ?? null,
                      },
                      status: {
                        set: OrderStatus.CONFIRMED,
                      },
                      orderComment: getCommentBlock(order, orderComment),
                      deliveryDate: toSaveDeliveryDate
                        ? {
                            set: toSaveDeliveryDate,
                          }
                        : defaultDeliveryDate
                          ? {
                              set: defaultDeliveryDate,
                            }
                          : undefined,
                      lastShippingDate: toSaveDeliveryDate
                        ? {
                            set: getLastShippingDateFromDeliveryDate(
                              toSaveDeliveryDate
                            ),
                          }
                        : defaultDeliveryDate
                          ? {
                              set: getLastShippingDateFromDeliveryDate(
                                defaultDeliveryDate
                              ),
                            }
                          : undefined,
                      orderBilling: {
                        create: [
                          {
                            user: {
                              connect: {
                                id: order.user.id,
                              },
                            },
                          },
                        ],
                      },
                    },
                  },
                });
              } catch (e) {
                console.error(e);
              }
            }}
            user={user}
          />
        );
      }
      return null;
    }
    case CreateOrderStep.Review:
      return <CreateOrderReview order={order} refetchOrder={refetchOrder} />;
    default:
      return (
        <div>
          Une erreur s&apos;est produite lors de la création de votre commande.
          Veuillez contacter l&apos;équipe Jasper.
        </div>
      );
  }
};
